import dayjs from 'dayjs';
import { Stack, styled, Typography } from '@mui/material';
import React, { memo, useCallback } from 'react';
import { StyledParams } from 'types/common';
import { TFile } from 'types/user';
import { EditIcon } from 'assets/icon';
import { ButtonCustom } from '../../../../../../components/elements/ButtonCustom';
import { InputText } from '../../../../../../components/elements/InputText';
import { bailiffUpdateFile } from 'services/files';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { useFlash } from 'contexts/flash';

export const FileSummary = ({
  file,
  reloadPage
}: {
  file?: Partial<TFile>;
  reloadPage?: () => void;
}) => {
  const { setFlash } = useFlash();
  const [showQuickNote, setShowQuickNote] = React.useState(false);
  const [textQuickNote, setTextQuickNote] = React.useState(file?.quickNote || '');

  const handleAddQuickNote = useCallback(async () => {
    if (!textQuickNote || !file?.id) return;
    const data = await bailiffUpdateFile(Number(file.id), { quickNote: textQuickNote });
    setFlash({ type: 'success', message: 'Add quick note successful' });
    const fileRes = data.file as TFile;
    reloadPage && reloadPage();
    setTextQuickNote(fileRes?.quickNote || '');
    setShowQuickNote(false);
  }, [textQuickNote]);

  const { asyncCallback: asyncAddQuickNote } = useAsyncCallback(handleAddQuickNote, []);

  const numberWithCommas = (money?: number) => {
    if (!money) return '';
    const parts = money.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };
  return (
    <Stack gap={'35px'}>
      <WrapperHeader>
        <Typography
          mb={'15px'}
          fontSize={16}
          fontWeight={600}
          color={'text.dark1'}
          lineHeight={'20px'}>
          File Details
        </Typography>
        <WrapperTimeAndButton>
          <StackTime>
            <img
              src={'/icons/icon_date_time.svg'}
              alt={'icon-date'}
              style={{ width: 20, height: 20 }}
            />
            <TextTimeStyled>{dayjs(file?.createdAt).format('YYYY/MM/DD | HH:mm A')}</TextTimeStyled>
          </StackTime>
        </WrapperTimeAndButton>
        <Stack
          mb={'15px'}
          direction="row"
          alignItems="start"
          justifyContent="space-between"
          gap={2}>
          <Stack flex={1}>
            {showQuickNote ? (
              <InputText
                placeholder={'Quick Note'}
                /* eslint-disable @typescript-eslint/no-explicit-any */
                onChange={(e: any) => {
                  setTextQuickNote(e.target.value);
                }}
                inputProps={{
                  value: textQuickNote,
                  sx: {
                    '& .MuiInputBase-input': {
                      backgroundColor: 'transparent !important'
                    }
                  },
                  multiline: true,
                  rows: 4,
                  style: {
                    background: 'transparent',
                    padding: 0,
                    backgroundColor: 'transparent'
                  }
                }}
              />
            ) : (
              <Typography fontSize={16} fontWeight={600} color={'text.dark1'} lineHeight={'20px'}>
                Quick Note: {file?.quickNote || ''}
              </Typography>
            )}
          </Stack>
          <Stack alignItems="center" gap={1}>
            {showQuickNote && (
              <ButtonCustom
                sx={{ minWidth: 58, height: 30, borderRadius: `4px !important` }}
                label={'Save'}
                onClick={() => {
                  asyncAddQuickNote();
                }}
              />
            )}
            <EditIcon
              onClick={() => setShowQuickNote(!showQuickNote)}
              style={{ cursor: 'pointer' }}
              width={30}
              height={30}
              fill={showQuickNote ? '#22374A' : '#fff'}
              color={showQuickNote ? '#fff' : '#22374A'}
            />
          </Stack>
        </Stack>
        <WrapperInfo>
          {[
            ['Client File Number', file?.fileNumber],
            ['Client', file?.clientOption?.value],
            ['Last Name', file?.lastName],
            ['First Name', file?.firstName],
            ['COBO', `${file?.coBorrowerFirstName || ''} ${file?.coBorrowerLastName || ''}`],
            ['Auction Delivery Location', file?.auctionDeliveryLocation?.value],
            ['Year, Make, Model', `${file?.year} ${file?.make} ${file?.model}`],
            ['VIN', file?.vin],
            ['Arrears', `$${numberWithCommas(file?.arrears)}`],
            ['Contract Balance', `$${numberWithCommas(file?.contractBalance)}`],
            ['Contract Date', dayjs(file?.contactDate).format('YYYY/MM/DD')],
            ['PPSA Base Registration Number', file?.ppsaBaseRegistrationNumber],
            ['PPSA Registration Date', dayjs(file?.ppsaRegistrationDate).format('YYYY/MM/DD')]
          ].map(([label, value]) => (
            <Stack key={label} direction="row" width="100%" gap={'20px'}>
              <Typography width={'60%'} color={'#6D6E70'}>
                {label}
              </Typography>
              <Typography color={'text.dark1'} sx={{ fontWeight: 500 }}>
                {value || ''}
              </Typography>
            </Stack>
          ))}
        </WrapperInfo>
      </WrapperHeader>
      <WrapperHeader>
        <Typography
          mb={'15px'}
          fontSize={16}
          fontWeight={600}
          color={'text.dark1'}
          lineHeight={'20px'}>
          Reminders
        </Typography>
        <Stack
          style={{
            border: '1px solid #A9ABAE',
            padding: '10px 15px',
            overflowY: 'auto',
            minHeight: '200px',
            maxHeight: '350px',
            borderRadius: '10px',
            gap: '10px'
          }}>
          {file?.reminders?.map((item, index) => {
            return (
              <Stack key={index}>
                <Typography
                  key={index}
                  color={'#888'}
                  fontSize={'14px'}
                  fontWeight={'500'}
                  lineHeight={'20px'}>
                  <Typography whiteSpace="pre-line" color={'#888'}>
                    {dayjs(item.date).format('YYYY/MM/DD hh:mm A')}
                  </Typography>
                </Typography>
                <Typography
                  ml={2}
                  key={index}
                  color={'#4B4B4B'}
                  fontSize={'14px'}
                  fontWeight={'500'}
                  lineHeight={'20px'}>
                  - {item.note}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </WrapperHeader>
    </Stack>
  );
};

export const FileSummaryMemo = memo(FileSummary);
const WrapperHeader = styled(Stack)(() => ({
  background: '#F0F1F2',
  padding: '20px 15px',
  borderRadius: '10px'
}));
const WrapperTimeAndButton = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingBottom: '22px'
}));
const StackTime = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '5px'
}));
const TextTimeStyled = styled(Typography)(({ theme }: StyledParams) => ({
  color: theme.colors.dark2,
  fontWeight: 500,
  lineHeight: '16.8px'
}));
const WrapperInfo = styled(Stack)(() => ({
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 8,
  paddingBottom: '20px'
}));
